import z, { ZodType } from 'zod'
import { asEnumType } from '@gqloom/zod'

// TODO(@jliocsar): Maybe these types should come from `shared/database`? Let's review that structure later
import type { AdminTable, BrandsTable } from '../backend/spanner'

export type BrandsTableZodSchema<TTableName extends keyof BrandsTable> = ZodType<
  BrandsTable[TTableName]
>

export type AdminTableZodSchema<TTableName extends keyof AdminTable> = ZodType<
  AdminTable[TTableName]
>

export const orderByDirectionEnumSchema = z.enum(['ASC', 'DESC']).register(asEnumType, {
  name: 'OrderByDirection',
  description: 'The direction of the order by',
})

class PaginationArgsSchemaBuilder<TSchema extends z.ZodRawShape> {
  constructor(private readonly schema: TSchema) {}

  private readonly searchSchema = {
    search: z.string().nullish(),
  } as const

  build() {
    return z.object(this.schema)
  }

  withOrderBy<TOrderBy extends z.ZodEnum>(
    orderBy: TOrderBy,
    defaultValue?: Array<{ column: z.infer<TOrderBy>; direction: 'ASC' | 'DESC' }>
  ): PaginationArgsSchemaBuilder<TSchema & ReturnType<typeof this.createOrderBySchema<TOrderBy>>> {
    const blessedSchema = z
      .object(this.schema)
      .extend(this.createOrderBySchema(orderBy, defaultValue))
    // @ts-expect-error(@jliocsar) ignoring this for now
    return new PaginationArgsSchemaBuilder(blessedSchema.shape) as PaginationArgsSchemaBuilder<
      typeof blessedSchema.shape
    >
  }

  withSearch(): PaginationArgsSchemaBuilder<TSchema & typeof this.searchSchema> {
    const blessedSchema = z.object(this.schema).extend(this.searchSchema)
    // @ts-expect-error(@jliocsar) ignoring this for now
    return new PaginationArgsSchemaBuilder(blessedSchema.shape) as PaginationArgsSchemaBuilder<
      typeof blessedSchema.shape
    >
  }

  private createOrderBySchema<TOrderBy extends z.ZodEnum>(
    orderBy: TOrderBy,
    defaultValue?: Array<{ column: z.infer<TOrderBy>; direction: 'ASC' | 'DESC' }>
  ) {
    const orderBySchema = z.array(
      z.object({
        column: orderBy,
        direction: orderByDirectionEnumSchema,
      })
    )
    return {
      orderBy: defaultValue ? orderBySchema.optional() : orderBySchema,
    }
  }
}

export const paginationCursorScalarSchema = z.base64()

export function createPaginationWithCursorArgsSchema() {
  const paginationArgsSchema = {
    cursor: paginationCursorScalarSchema.optional(),
    pageSize: z.number().optional(),
  }

  return new PaginationArgsSchemaBuilder(paginationArgsSchema)
}

export function createPaginationWithCursorResultSchema<TSchema>(rowSchema: z.ZodType<TSchema>) {
  return z.object({
    pageInfo: z.object({
      startCursor: paginationCursorScalarSchema.nullish(),
      endCursor: paginationCursorScalarSchema.nullish(),
      totalRecords: z.int(),
      hasNextPage: z.boolean(),
    }),
    results: z.array(
      z.object({
        row: rowSchema,
        cursor: paginationCursorScalarSchema,
      })
    ),
  })
}

export function createPaginationWithCurrentPageResultSchema<TSchema>(
  rowSchema: z.ZodType<TSchema>
) {
  return z.object({
    pageInfo: z.object({
      totalRecords: z.int(),
      hasNextPage: z.boolean(),
    }),
    results: z.array(z.object(rowSchema)),
  })
}

export type PaginationWithCursorArgsSchema = z.infer<
  ReturnType<typeof createPaginationWithCursorArgsSchema>
>

export type PaginationWithCursorResultSchema<TSchema> = z.infer<
  ReturnType<typeof createPaginationWithCursorResultSchema<TSchema>>
>

export type PaginationWithCurrentPageResultSchema<TSchema> = z.infer<
  ReturnType<typeof createPaginationWithCurrentPageResultSchema<TSchema>>
>
